import { useItemForm } from "../model/ItemFormContext";
import type { UserConfigInputs } from "../../user-settings/model/user-config.types";
import { CircleAlert } from "lucide-react";

type PriceConfig = Pick<UserConfigInputs,
  "adjust_no_exposed" | "skip_low_coupon" | "adjust_max_step" |
  "reset_max_step" | "reset_on_ll" | "reset_on_ll_countime"
>;

interface ItemPriceAdjustmentFieldsProps {
  isPremium: boolean;
  isSmartstore: boolean;
  isLotteon: boolean;
  isSsgdotcom?: boolean;
  userAdjustConfig: PriceConfig | null;
}

const ItemPriceAdjustmentFields = ({ isPremium, isSmartstore, isLotteon, isSsgdotcom = false, userAdjustConfig }: ItemPriceAdjustmentFieldsProps) => {
  const priceOnly = isLotteon || isSsgdotcom;
  const unsupportedLabel = isSsgdotcom ? "(SSG닷컴 미지원)" : "(롯데ON 미지원)";
  const { register, watch, trigger, errors, getValues } = useItemForm();
  const adjustStrategy = watch("adjust_strategy");
  const gapPriceBasis = watch("gap_price_basis");
  const adjustNoExposedUseGlobal = watch("adjust_no_exposed.use_global");
  const skipLowCouponUseGlobal = watch("skip_low_coupon.use_global");
  const adjustMaxStepUseGlobal = watch("adjust_max_step.use_global");
  const resetMaxStepUseGlobal = watch("reset_max_step.use_global");
  const resetOnLlUseGlobal = watch("reset_on_ll.use_global");
  const resetOnLlCountimeUseGlobal = watch("reset_on_ll_countime.use_global");
  const adjustPriceExposedRangeIsEnabled = watch("adjust_price_exposed_range.is_enabled");
  const competitionGapLowerType = watch("competition_gap_limit.lower.value_type");
  const competitionGapUpperType = watch("competition_gap_limit.upper.value_type");

  const validateAdjustDiscountRange = (
    field: "value_min" | "value_max",
    value: number,
  ) => {
    const range = getValues("adjust_discount_range");
    const valueMin = Number(range?.value_min ?? 0);
    const valueMax = Number(range?.value_max ?? 0);

    if (valueMin === 0 || valueMax === 0) return true;
    if (field === "value_min" && value >= valueMax) return "하한은 상한보다 작아야 합니다.";
    if (field === "value_max" && value <= valueMin) return "상한은 하한보다 커야 합니다.";
    return true;
  };

  const validateAdjustPriceRange = (
    field: "value_min" | "value_max",
    value: number,
  ) => {
    const range = getValues("adjust_price_range");
    const valueMin = Number(range?.value_min ?? 0);
    const valueMax = Number(range?.value_max ?? 0);

    if (valueMin === 0 || valueMax === 0) return true;
    if (field === "value_min" && value >= valueMax) return "하한은 상한보다 작아야 합니다.";
    if (field === "value_max" && value <= valueMin) return "상한은 하한보다 커야 합니다.";
    return true;
  };

  const validateCompetitionGapLimit = (
    direction: "lower" | "upper",
    value: number,
  ) => {
    if (!Number.isFinite(value) || value < 0) return "0 이상의 숫자를 입력하세요.";
    const valueType = getValues(`competition_gap_limit.${direction}.value_type`);
    if (valueType === "amount" && !Number.isInteger(value)) {
      return "정액은 정수로 입력하세요.";
    }
    return true;
  };

  return (
      <div className="row px-0 mt-3">
        <div className="col">
          <fieldset className="border-0 p-0 m-0">
            <legend className="float-none w-auto fs-6 fw-semibold mb-3 px-0">가격 조정</legend>
          <ul className="list-unstyled">
            {/* 희망 차액 */}
            <li className="mb-3">
              <label className="form-label">
                희망 차액{" "}
                <span className="form-text">
                  (음수는 더 낮게, 양수는 더 높게 조정, 0으로 설정 시 동일가)
                </span>
              </label>
              <div className="row">
                <div className="col-auto">
                  <div className="input-group">
                    <input
                      type="number"
                      step="10"
                      id="id_desired_gap"
                      className={`form-control ${errors.desired_gap ? "is-invalid" : ""}`}
                      {...register("desired_gap", {
                        required: "목표 차액를 입력하세요.",
                        valueAsNumber: true,
                        validate: (value: number) => {
                          if (value % 10 !== 0) {
                            return "목표 차액는 10의 배수여야 합니다.";
                          }
                          return true;
                        },
                      })}
                    />
                    <span className="input-group-text">원</span>
                  </div>
                </div>
              </div>
              {errors.desired_gap && (
                <div className="invalid-feedback">
                  {errors.desired_gap.message}
                </div>
              )}
            </li>
            <li className="mb-3">
              <div className="row mb-3">
                {/* 조정 방식 */}
                <div className="col-auto">
                  <label className="form-label">가격 조정 방식</label>
                  <select
                    className="form-select"
                    {...register("adjust_strategy")}
                  >
                    <option value="adjust_price">판매가 조정</option>
                    <option value="adjust_discount" disabled={priceOnly}>
                      할인액 조정 {priceOnly ? unsupportedLabel : ""}
                    </option>
                    <option
                      value="adjust_price_match_discount"
                      disabled={
                        priceOnly ||
                        !isPremium ||
                        gapPriceBasis !== "price"
                      }
                    >
                      할인가 맞춤 판매가 조정{" "}
                      {priceOnly
                        ? unsupportedLabel
                        : !isPremium
                        ? "(프로 전용)"
                        : gapPriceBasis !== "price"
                          ? "(차액 판매가 기준 전용)"
                          : ""}
                    </option>
                    <option
                      value="adjust_discount_match_price"
                      disabled={
                        priceOnly ||
                        !isPremium ||
                        gapPriceBasis !== "price"
                      }
                    >
                      판매가 맞춤 할인액 조정{" "}
                      {priceOnly
                        ? unsupportedLabel
                        : !isPremium
                        ? "(프로 전용)"
                        : gapPriceBasis !== "price"
                          ? "(차액 판매가 기준 전용)"
                          : ""}
                    </option>
                  </select>
                </div>
              </div>
            </li>
            {/* 경쟁 방식에 따른 제한 구분: 판매가 제한, 할인가 제한 */}
            {(adjustStrategy === "adjust_price" ||
              adjustStrategy === "adjust_price_match_discount") && (
                <li className="mb-3">
                  <label className="form-label">판매가 상하한</label>
                  <div className="row">
                    <div className="col-auto">
                      <div className="d-flex gap-2">
                        <input
                          type="hidden"
                          {...register(
                            "adjust_price_range.value_min_type",
                          )} /*value="amount"*/
                        />
                        <div className="input-group">
                          <span className="input-group-text">하한</span>
                          <input
                            type="number"
                            min={0}
                            step={10}
                            placeholder="0"
                            className={`form-control ${errors.adjust_price_range?.value_min ? "is-invalid" : ""}`}
                            style={{ maxWidth: "160px" }}
                            {...register("adjust_price_range.value_min", {
                              valueAsNumber: true,
                              min: {
                                value: 0,
                                message: "0 이상이어야 합니다.",
                              },
                              onChange: () => {
                                void trigger([
                                  "adjust_price_range.value_min",
                                  "adjust_price_range.value_max",
                                ]);
                              },
                              validate: (value: number) =>
                                validateAdjustPriceRange("value_min", value),
                            })}
                            disabled={adjustPriceExposedRangeIsEnabled}
                          />
                          <span className="input-group-text">원</span>
                        </div>
                      </div>
                      {errors.adjust_price_range?.value_min && (
                        <div className="invalid-feedback d-block">
                          {errors.adjust_price_range.value_min.message}
                        </div>
                      )}
                      <div className="form-text">0으로 설정 시 제한없음</div>
                    </div>
                    <div className="col-auto">
                      <div className="d-flex gap-2">
                        <input
                          type="hidden"
                          {...register("adjust_price_range.value_max_type")}
                        />
                        <div className="input-group">
                          <span className="input-group-text">상한</span>
                          <input
                            type="number"
                            min={0}
                            step={10}
                            placeholder="0"
                            className={`form-control ${errors.adjust_price_range?.value_max ? "is-invalid" : ""}`}
                            style={{ maxWidth: "160px" }}
                            {...register("adjust_price_range.value_max", {
                              valueAsNumber: true,
                              min: {
                                value: 0,
                                message: "0 이상이어야 합니다.",
                              },
                              onChange: () => {
                                void trigger([
                                  "adjust_price_range.value_min",
                                  "adjust_price_range.value_max",
                                ]);
                              },
                              validate: (value: number) =>
                                validateAdjustPriceRange("value_max", value),
                            })}
                            disabled={adjustPriceExposedRangeIsEnabled}
                          />
                          <span className="input-group-text">원</span>
                        </div>
                      </div>
                      {errors.adjust_price_range?.value_max && (
                        <div className="invalid-feedback d-block">
                          {errors.adjust_price_range.value_max.message}
                        </div>
                      )}
                      <div className="form-text">0으로 설정 시 제한없음</div>
                    </div>
                  </div>
                </li>
              )}
            {(adjustStrategy === "adjust_discount" ||
              adjustStrategy === "adjust_discount_match_price") && (
                <li className="mb-3">
                  <label className="form-label">할인액 조정 하한/상한</label>
                  <div className="row">
                    <div className="col-auto">
                      <div className="d-flex gap-2">
                        <div className="input-group">
                          <span className="input-group-text">하한</span>
                          <input
                            type="number"
                            min={0}
                            step={10}
                            className={`form-control ${errors.adjust_discount_range?.value_min ? "is-invalid" : ""}`}
                            style={{ maxWidth: "160px" }}
                            {...register("adjust_discount_range.value_min", {
                              valueAsNumber: true,
                              min: {
                                value: 0,
                                message: "0 이상이어야 합니다.",
                              },
                              onChange: () => {
                                void trigger([
                                  "adjust_discount_range.value_min",
                                  "adjust_discount_range.value_max",
                                ]);
                              },
                              validate: (value: number) =>
                                validateAdjustDiscountRange("value_min", value),
                            })}
                            disabled={adjustPriceExposedRangeIsEnabled}
                          />
                          <span className="input-group-text">원</span>
                        </div>
                      </div>
                      {errors.adjust_discount_range?.value_min && (
                        <div className="invalid-feedback d-block">
                          {errors.adjust_discount_range.value_min.message}
                        </div>
                      )}
                      <div className="form-text">0으로 설정 시 제한없음</div>
                    </div>
                    <div className="col-auto">
                      <div className="d-flex gap-2">
                        <div className="input-group">
                          <span className="input-group-text">상한</span>
                          <input
                            type="number"
                            min={0}
                            max={isSmartstore ? 10000000 - 10 : undefined}
                            step={10}
                            className={`form-control ${errors.adjust_discount_range?.value_max ? "is-invalid" : ""}`}
                            style={{ maxWidth: "160px" }}
                            {...register("adjust_discount_range.value_max", {
                              valueAsNumber: true,
                              min: {
                                value: 0,
                                message: "0 이상이어야 합니다.",
                              },
                              ...(isSmartstore
                                ? {
                                    max: {
                                      value: 10000000 - 10,
                                      message: "(스마트스토어 최대 1천만원)",
                                    },
                                  }
                                : {}),
                              onChange: () => {
                                void trigger([
                                  "adjust_discount_range.value_min",
                                  "adjust_discount_range.value_max",
                                ]);
                              },
                              validate: (value: number) =>
                                validateAdjustDiscountRange("value_max", value),
                            })}
                            disabled={adjustPriceExposedRangeIsEnabled}
                          />
                        </div>
                        <select
                          className="form-select"
                          {...register("adjust_discount_range.value_max_type")}
                          style={{ width: "80px" }}
                        >
                          <option value="percent">%</option>
                          <option value="amount">원</option>
                        </select>
                      </div>
                      {errors.adjust_discount_range?.value_max && (
                        <div className="invalid-feedback d-block">
                          {errors.adjust_discount_range.value_max.message}
                        </div>
                      )}
                      <div className="form-text">
                        0으로 설정 시 제한없음
                        {isSmartstore && (<span className="small"> (스마트스토어 최대 1천만원)</span>)}
                      </div>
                    </div>
                  </div>
                </li>
              )}
            {/* 노출가 하한 */}
            <li className="mb-3">
              <label className="form-label">노출가 하한</label>
              <div className="row">
                <div className="col-auto">
                  <div className="d-flex gap-2">
                    <input
                      type="hidden"
                      {...register(
                        "adjust_price_exposed_range.value_min_type",
                      )} /*value="amount"*/
                    />
                    <div className="input-group">
                      <input
                        type="number"
                        min={0}
                        step={10}
                        className="form-control"
                        {...register("adjust_price_exposed_range.value_min", {
                          valueAsNumber: true,
                          min: {
                            value: 0,
                            message: "0 이상이어야 합니다.",
                          },
                        })}
                        disabled={!adjustPriceExposedRangeIsEnabled}
                      />
                      <span className="input-group-text">원</span>
                    </div>
                  </div>
                  <div className="form-text">0으로 설정 시 제한없음</div>
                </div>
                <div className="col-auto">
                  <div className="form-check" style={{ minHeight: "38px" }}>
                    <input
                      id="adjust_price_exposed_range_is_enabled"
                      type="checkbox"
                      className="form-check-input"
                      {...register("adjust_price_exposed_range.is_enabled")}
                    />
                    <label htmlFor="adjust_price_exposed_range_is_enabled">
                      활성화
                    </label>
                  </div>
                </div>
              </div>
            </li>
          </ul>
          </fieldset>
        </div>

        <div className="col">
          <fieldset className="border-0 p-0 m-0">
            <legend className="float-none w-auto fs-6 fw-semibold mb-3 px-0">조정 제한·복구</legend>
          {/* 비노출시 경쟁 */}
          <div className="d-flex gap-3 mb-2 justify-content-between">
            <div className="form-check form-switch">
              <input
                className={`form-check-input ${adjustNoExposedUseGlobal ? "" : "d-none"}`}
                type="checkbox"
                role="switch"
                checked={
                  userAdjustConfig?.adjust_no_exposed.is_enabled || false
                }
                disabled
              />
              <input
                className={`form-check-input ${adjustNoExposedUseGlobal ? "d-none" : ""}`}
                type="checkbox"
                role="switch"
                id="adjust_no_exposed_enabled"
                {...register("adjust_no_exposed.is_enabled")}
              />
              <label className="form-label">
                가격비교에 내 상품 비노출 시에도 가격조정
              </label>
            </div>
            <div className="form-check form-check-reverse">
              <input
                className="form-check-input"
                type="checkbox"
                id="adjust_no_exposed_use_global"
                {...register("adjust_no_exposed.use_global")}
              />
              <label className="form-label small">환경설정에 따름</label>
            </div>
          </div>

          {/* 쿠폰율에 따른 경쟁제한 */}
          <div className="d-flex gap-3 justify-content-between">
            <div className="form-check form-switch">
              <input
                className={`form-check-input ${skipLowCouponUseGlobal ? "" : "d-none"}`}
                type="checkbox"
                role="switch"
                checked={userAdjustConfig?.skip_low_coupon.is_enabled || false}
                disabled
              />
              <input
                className={`form-check-input ${skipLowCouponUseGlobal ? "d-none" : ""}`}
                type="checkbox"
                role="switch"
                {...register("skip_low_coupon.is_enabled")}
              />
              <label className="form-label">쿠폰율에 따른 가격조정 제한</label>
            </div>
            <div className="form-check form-check-reverse">
              <input
                className="form-check-input"
                type="checkbox"
                {...register("skip_low_coupon.use_global")}
              />
              <label className="form-label small">환경설정에 따름</label>
            </div>
          </div>
          <div className="mb-3" style={{ marginLeft: "40px" }}>
            <div
              className={`input-group ${skipLowCouponUseGlobal ? "" : "d-none"}`}
              style={{ width: "200px" }}
            >
              <input
                type="number"
                step="0.01"
                className="form-control"
                disabled
                value={userAdjustConfig?.skip_low_coupon?.value || 0}
              />
              <span className="input-group-text">%</span>
            </div>
            <div
              className={`input-group ${skipLowCouponUseGlobal ? "d-none" : ""}`}
              style={{ width: "200px" }}
            >
              <input
                type="number"
                step="0.01"
                className="form-control"
                {...register("skip_low_coupon.value", { valueAsNumber: true })}
              />
              <span className="input-group-text">%</span>
            </div>
            <div className="form-text">
              쿠폰율이 설정된 퍼센트 미만인 경우 가격 경쟁 제외
            </div>
          </div>

          {/* 변동폭 제한: 가격 경쟁 시 */}
          <div className="d-flex gap-3 justify-content-between">
            <div className="form-check form-switch">
              <input
                className={`form-check-input ${adjustMaxStepUseGlobal ? "" : "d-none"}`}
                type="checkbox"
                role="switch"
                checked={userAdjustConfig?.adjust_max_step.is_enabled || false}
                disabled
              />
              <input
                className={`form-check-input ${adjustMaxStepUseGlobal ? "d-none" : ""}`}
                type="checkbox"
                role="switch"
                {...register("adjust_max_step.is_enabled")}
              />
              <label className="form-label">가격 경쟁 시 변동폭 제한</label>
              <span
                className="ms-1 text-muted"
                title="맞춤 전략에는 변동폭 제한이 적용되지 않습니다."
                aria-label="맞춤 전략에는 변동폭 제한이 적용되지 않습니다."
                tabIndex={0}
              >
                <CircleAlert size={15} />
              </span>
            </div>
            <div className="form-check form-check-reverse">
              <input
                className="form-check-input"
                type="checkbox"
                id="adjust_max_step_use_global"
                {...register("adjust_max_step.use_global")}
              />
              <label className="form-label small">환경설정에 따름</label>
            </div>
          </div>
          <div className="mb-3" style={{ marginLeft: "40px" }}>
            <div
              className={`input-group ${adjustMaxStepUseGlobal ? "" : "d-none"}`}
              style={{ width: "200px" }}
            >
              <input
                type="number"
                step="0.01"
                className="form-control"
                disabled
                value={userAdjustConfig?.adjust_max_step?.value || 0}
              />
              <span className="input-group-text">%</span>
            </div>
            <div
              className={`input-group ${adjustMaxStepUseGlobal ? "d-none" : ""}`}
              style={{ width: "200px" }}
            >
              <input
                type="number"
                step="0.01"
                className="form-control"
                min={0}
                {...register("adjust_max_step.value", { valueAsNumber: true })}
              />
              <span className="input-group-text">%</span>
            </div>
          </div>

          {/* 하한가 가격 복구 */}
          <div className="d-flex gap-3 mb-2 justify-content-between">
            <div className="form-check form-switch">
              <input
                className={`form-check-input ${resetOnLlUseGlobal ? "" : "d-none"}`}
                type="checkbox"
                role="switch"
                checked={userAdjustConfig?.reset_on_ll.is_enabled || false}
                disabled
              />
              <input
                className={`form-check-input ${resetOnLlUseGlobal ? "d-none" : ""}`}
                type="checkbox"
                role="switch"
                {...register("reset_on_ll.is_enabled")}
              />
              <label className="form-label">
                하한가 도달 시 복구기준가로 복구
              </label>
            </div>
            <div className="form-check form-check-reverse">
              <input
                className="form-check-input"
                type="checkbox"
                id="reset_on_ll_use_global"
                {...register("reset_on_ll.use_global")}
              />
              <label className="form-label small">환경설정에 따름</label>
            </div>
          </div>

          {/* 변동폭 제한: 가격 복구 시 */}
          <div className="d-flex gap-3 justify-content-between">
            <div className="form-check form-switch">
              <input
                className={`form-check-input ${resetMaxStepUseGlobal ? "" : "d-none"}`}
                type="checkbox"
                role="switch"
                checked={userAdjustConfig?.reset_max_step.is_enabled || false}
                disabled
              />
              <input
                className={`form-check-input ${resetMaxStepUseGlobal ? "d-none" : ""}`}
                type="checkbox"
                role="switch"
                {...register("reset_max_step.is_enabled")}
              />
              <label className="form-label">
                    하한가 도달 시 복구기준가로 복구 시 변동폭 제한
              </label>
            </div>
            <div className="form-check form-check-reverse">
              <input
                className="form-check-input"
                type="checkbox"
                id="reset_max_step_use_global"
                {...register("reset_max_step.use_global")}
              />
              <label className="form-label small">환경설정에 따름</label>
            </div>
          </div>
          <div className="mb-3" style={{ marginLeft: "40px" }}>
            <div
              className={`input-group ${resetMaxStepUseGlobal ? "" : "d-none"}`}
              style={{ width: "200px" }}
            >
              <input
                type="number"
                step="0.01"
                className="form-control"
                disabled
                value={userAdjustConfig?.reset_max_step?.value || 0}
              />
              <span className="input-group-text">%</span>
            </div>
            <div
              className={`input-group ${resetMaxStepUseGlobal ? "d-none" : ""}`}
              style={{ width: "200px" }}
            >
              <input
                type="number"
                step="0.01"
                className="form-control"
                min={0}
                {...register("reset_max_step.value", { valueAsNumber: true })}
              />
              <span className="input-group-text">%</span>
            </div>
          </div>

          {/* 하한가 가격 복구: 경쟁 대기 */}
          <div className="d-flex gap-3 justify-content-between">
            <div className="form-check form-switch">
              <input
                className={`form-check-input ${resetOnLlCountimeUseGlobal ? "" : "d-none"}`}
                type="checkbox"
                role="switch"
                checked={
                  userAdjustConfig?.reset_on_ll_countime.is_enabled || false
                }
                disabled
              />
              <input
                className={`form-check-input ${resetOnLlCountimeUseGlobal ? "d-none" : ""}`}
                type="checkbox"
                role="switch"
                {...register("reset_on_ll_countime.is_enabled")}
              />
              <label className="form-label">
                하한가 도달 시 복구기준가로 복구 후 경쟁 대기
              </label>
            </div>
            <div className="form-check form-check-reverse">
              <input
                className="form-check-input"
                type="checkbox"
                id="reset_on_ll_countime_use_global"
                {...register("reset_on_ll_countime.use_global")}
              />
              <label className="form-label small">환경설정에 따름</label>
            </div>
          </div>
          <div className="d-flex mb-3" style={{ marginLeft: "40px" }}>
            <div
              className={`input-group ${resetOnLlCountimeUseGlobal ? "" : "d-none"}`}
              style={{ width: "200px" }}
            >
              <input
                type="number"
                step="5"
                className="form-control"
                disabled
                value={userAdjustConfig?.reset_on_ll_countime?.value || 5}
              />
              <span className="input-group-text">분</span>
            </div>
            <div
              className={`input-group ${resetOnLlCountimeUseGlobal ? "d-none" : ""}`}
              style={{ width: "200px" }}
            >
              <input
                type="number"
                step="5"
                className="form-control"
                min={5}
                {...register("reset_on_ll_countime.value", {
                  valueAsNumber: true,
                })}
              />
              <span className="input-group-text">분</span>
            </div>
          </div>

          {/* 차액 범위 초과 경쟁제외: 상품 로컬 전용 */}
          <div className="form-check form-switch">
            <input
              type="hidden"
              {...register("competition_gap_limit.mode")}
            />
            <input
              type="hidden"
              {...register("competition_gap_limit.basis")}
            />
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="competition_gap_limit_enabled"
              {...register("competition_gap_limit.is_enabled", {
                validate: (enabled: boolean) =>
                  !enabled
                  || getValues("competition_gap_limit.lower.is_enabled")
                  || getValues("competition_gap_limit.upper.is_enabled")
                    ? true
                    : "타겟상품이 더 저렴 또는 더 비쌈을 하나 이상 활성화하세요.",
              })}
            />
            <label className="form-label" htmlFor="competition_gap_limit_enabled">
              차액 크기에 따른 가격조정 제한
            </label>
          </div>
          {errors.competition_gap_limit?.is_enabled && (
            <div className="invalid-feedback d-block" style={{ marginLeft: "40px" }}>
              {errors.competition_gap_limit.is_enabled.message}
            </div>
          )}
          <div className="mb-3" style={{ marginLeft: "40px" }}>
            {(["upper", "lower"] as const).map((direction) => {
              const valueType = direction === "lower"
                ? competitionGapLowerType
                : competitionGapUpperType;
              const fieldError = errors.competition_gap_limit?.[direction]?.value;
              return (
                <div className="mb-2" key={direction}>
                  <div className="d-flex gap-2 align-items-center">
                    <div className="form-check mb-0" style={{ minWidth: "180px" }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`competition_gap_${direction}_enabled`}
                        {...register(`competition_gap_limit.${direction}.is_enabled`, {
                          onChange: () => {
                            void trigger("competition_gap_limit.is_enabled");
                          },
                        })}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`competition_gap_${direction}_enabled`}
                      >
                        {direction === "upper"
                          ? "타겟상품이 더 저렴"
                          : "타겟상품이 더 비쌈"}
                      </label>
                    </div>
                    <input
                      type="number"
                      min={0}
                      step={valueType === "amount" ? 10 : 0.01}
                      className={`form-control ${fieldError ? "is-invalid" : ""}`}
                      style={{ maxWidth: "150px" }}
                      {...register(`competition_gap_limit.${direction}.value`, {
                        valueAsNumber: true,
                        validate: (value: number) =>
                          validateCompetitionGapLimit(direction, value),
                      })}
                    />
                    <select
                      className="form-select"
                      style={{ width: "80px" }}
                      {...register(`competition_gap_limit.${direction}.value_type`)}
                    >
                      <option value="percent">%</option>
                      <option value="amount">원</option>
                    </select>
                  </div>
                  {fieldError && (
                    <div className="invalid-feedback d-block">
                      {fieldError.message}
                    </div>
                  )}
                </div>
              );
            })}
            <div className="form-text">
              타겟상품과 내상품의 차액을 기준으로 계산합니다.
            </div>
          </div>
          </fieldset>
        </div>
      </div>

  );
};

export default ItemPriceAdjustmentFields;
